import * as React from "react"

export interface ThemeCSSVars {
  [key: string]: string
}

export interface ThemeJSON {
  light?: ThemeCSSVars
  dark?: ThemeCSSVars
}

export interface ThemeContextValue {
  theme: ThemeJSON | null
  setTheme: (theme: ThemeJSON) => void
  mode: "light" | "dark"
  setMode: (mode: "light" | "dark") => void
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(
  undefined
)

export function useTheme() {
  const context = React.useContext(ThemeContext)
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider")
  }
  return context
}

function applyCSSVariables(vars: ThemeCSSVars, selector: string = ":root") {
  const root =
    (document.querySelector(selector) as HTMLElement) ||
    document.documentElement
  Object.entries(vars).forEach(([key, value]) => {
    root.style.setProperty(key, value)
  })
}

function applyTheme(theme: ThemeJSON, mode: "light" | "dark") {
  requestAnimationFrame(() => {
    const vars = mode === "dark" ? theme.dark : theme.light
    if (vars) {
      applyCSSVariables(vars)
    }

    if (mode === "dark") {
      document.documentElement.classList.add("dark")
    } else {
      document.documentElement.classList.remove("dark")
    }
  })
}

export interface ThemeProviderProps {
  theme?: ThemeJSON
  defaultMode?: "light" | "dark"
  children: React.ReactNode
}

export const ThemeProvider = ({
  theme: initialTheme,
  defaultMode = "light",
  children
}: ThemeProviderProps) => {
  const [theme, setThemeState] = React.useState<ThemeJSON | null>(
    initialTheme || null
  )
  const [mode, setModeState] = React.useState<"light" | "dark">(defaultMode)

  React.useEffect(() => {
    if (theme) {
      applyTheme(theme, mode)
    }
  }, [theme, mode])

  const setTheme = React.useCallback(
    (newTheme: ThemeJSON) => {
      setThemeState(newTheme)
      applyTheme(newTheme, mode)
    },
    [mode]
  )

  const setMode = React.useCallback(
    (newMode: "light" | "dark") => {
      setModeState(newMode)
      if (theme) {
        applyTheme(theme, newMode)
      }
    },
    [theme]
  )

  const value = React.useMemo(
    () => ({
      theme,
      setTheme,
      mode,
      setMode
    }),
    [theme, setTheme, mode, setMode]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
